import React, { useState, useRef, useEffect } from 'react';
import { 
  Filter, 
  X, 
  Search, 
  Circle 
} from 'lucide-react';
import './style.css';
import { lang } from '../../../utils/langs';

const FacetedFilter = ({ 
  filters = [], 
  activeFilters = [], 
  onChange, 
  onClear 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submenuSearchQuery, setSubmenuSearchQuery] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [hoveredText, setHoveredText] = useState('');
  const [tooltipCoords, setTooltipCoords] = useState({ top: 0, left: 0 });
  const [tempDate, setTempDate] = useState('');
  const [tempText, setTempText] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown on clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedProperty(null);
        setSearchQuery('');
        setSubmenuSearchQuery('');
        setHoveredText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = (e, text) => {
    if (text.length < 15) return; // Only show tooltip for long/truncated texts
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredText(text);
    setTooltipCoords({
      top: rect.top - 6,
      left: rect.left + rect.width / 2
    });
  };

  const handlePropertyClick = (prop) => {
    if ((prop.options && prop.options.length > 0) || prop.type === 'date' || prop.type === 'text') {
      setSelectedProperty(prop);
      if (prop.type === 'date') {
        setTempDate(new Date().toISOString().split('T')[0]);
      } else if (prop.type === 'text') {
        setTempText('');
      }
    } else {
      addFilter(prop.key, 'is', 'true');
      setIsOpen(false);
    }
  };


  const addFilter = (key, operator, value) => {
    const prop = filters.find(f => f.key === key);
    const newFilter = {
      key,
      label: prop ? prop.label : key,
      icon: prop ? prop.icon : null,
      operator,
      value
    };
    
    // Prevent duplicates
    const exists = activeFilters.some(f => f.key === key && f.value === value);
    if (!exists) {
      onChange([...activeFilters, newFilter]);
    }
    setIsOpen(false);
    setSelectedProperty(null);
    setSearchQuery('');
    setSubmenuSearchQuery('');
    setHoveredText('');
  };

  const removeFilter = (indexToRemove) => {
    onChange(activeFilters.filter((_, idx) => idx !== indexToRemove));
  };

  const getDisplayValue = (filter) => {
    const prop = filters.find(f => f.key === filter.key);
    if (prop && prop.options) {
      const foundOpt = prop.options.find(
        (o) => (typeof o === 'object' ? o.value : o) === filter.value
      );
      if (foundOpt) {
        return typeof foundOpt === 'object' ? foundOpt.label : foundOpt;
      }
    }
    return filter.value;
  };

  const filteredProperties = filters.filter(prop => 
    prop.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="faceted-filter-container" ref={dropdownRef}>
      <div className="faceted-filter-active-list">
        {activeFilters.map((filter, idx) => (
          <div key={idx} className="faceted-filter-pill">
            {filter.icon && <span className="pill-icon">{filter.icon}</span>}
            <span className="pill-label">{filter.label}</span>
            <span className="pill-operator">{filter.operator || 'is'}</span>
            <span className="pill-value">{getDisplayValue(filter)}</span>
            <button className="pill-remove" onClick={() => removeFilter(idx)} aria-label="Remover filtro">
              <X size={12} />
            </button>
          </div>
        ))}

        {activeFilters.length > 0 && (
          <button className="faceted-filter-clear-all" onClick={onClear}>
            {lang('es', 'Clear') || 'Clear'}
          </button>
        )}

        <button 
          className={`faceted-filter-trigger ${isOpen ? 'active' : ''}`}
          onClick={() => {
            setIsOpen(!isOpen);
            setSelectedProperty(null);
            setSearchQuery('');
            setSubmenuSearchQuery('');
            setHoveredText('');
          }}
          aria-label="Filtrar datos"
        >
          <Filter size={14} />
          <span className="trigger-text">{lang('es', 'Filtrar') || 'Filtrar'}</span>
        </button>
      </div>

      {isOpen && (
        <div className="faceted-filter-dropdown animate-in">
          {!selectedProperty ? (
            <>
              <div className="dropdown-search-wrapper">
                <Search size={14} className="dropdown-search-icon" />
                <input 
                  type="text"
                  placeholder="Filter..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="dropdown-search-input"
                  autoFocus
                />
              </div>
              <div className="dropdown-list">
                {filteredProperties.map(prop => (
                  <button 
                    key={prop.key} 
                    className="dropdown-item"
                    onMouseEnter={(e) => handleMouseEnter(e, prop.label)}
                    onMouseLeave={() => setHoveredText('')}
                    onClick={() => handlePropertyClick(prop)}
                  >
                    <span className="item-icon">{prop.icon || <Circle size={14} />}</span>
                    <span className="item-label">{prop.label}</span>
                  </button>
                ))}
                {filteredProperties.length === 0 && (
                  <div className="dropdown-no-results">No results</div>
                )}
              </div>
            </>
          ) : (
            <div className="dropdown-submenu">
              <div className="submenu-header">
                <button className="submenu-back-btn" onClick={() => { setSelectedProperty(null); setSubmenuSearchQuery(''); setHoveredText(''); }}>
                  &larr; {selectedProperty.label}
                </button>
              </div>
              {selectedProperty.type !== 'date' && selectedProperty.type !== 'text' && (
                <div className="dropdown-search-wrapper">
                  <Search size={14} className="dropdown-search-icon" />
                  <input 
                    type="text"
                    placeholder={`Search ${selectedProperty.label}...`}
                    value={submenuSearchQuery}
                    onChange={(e) => setSubmenuSearchQuery(e.target.value)}
                    className="dropdown-search-input"
                    autoFocus
                  />
                </div>
              )}

              <div className="dropdown-list">
                {selectedProperty.type === 'date' ? (
                  <div className="dropdown-date-wrapper">
                    <input 
                      type="date"
                      className="dropdown-date-input"
                      value={tempDate}
                      onChange={(e) => setTempDate(e.target.value)}
                      autoFocus
                    />
                    <button 
                      className="dropdown-date-confirm-btn"
                      onClick={() => {
                        if (tempDate) {
                          addFilter(selectedProperty.key, 'is', tempDate);
                        }
                      }}
                    >
                      {lang('es', 'Aplicar') || 'Aplicar'}
                    </button>
                  </div>
                ) : selectedProperty.type === 'text' ? (
                  <div className="dropdown-date-wrapper">
                    <input 
                      type="text"
                      placeholder={`Ingresar ${selectedProperty.label.toLowerCase()}...`}
                      className="dropdown-date-input"
                      value={tempText}
                      onChange={(e) => setTempText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && tempText) {
                          addFilter(selectedProperty.key, 'is', tempText);
                        }
                      }}
                      autoFocus
                    />
                    <button 
                      className="dropdown-date-confirm-btn"
                      onClick={() => {
                        if (tempText) {
                          addFilter(selectedProperty.key, 'is', tempText);
                        }
                      }}
                    >
                      {lang('es', 'Aplicar') || 'Aplicar'}
                    </button>
                  </div>
                ) : (

                  selectedProperty.options
                    ?.filter(opt => {
                      const label = typeof opt === 'object' ? opt.label : opt;
                      return label.toLowerCase().includes(submenuSearchQuery.toLowerCase());
                    })
                    .map(opt => {
                      const label = typeof opt === 'object' ? opt.label : opt;
                      const value = typeof opt === 'object' ? opt.value : opt;
                      return (
                        <button 
                          key={value} 
                          className="dropdown-item"
                          onMouseEnter={(e) => handleMouseEnter(e, label)}
                          onMouseLeave={() => setHoveredText('')}
                          onClick={() => addFilter(selectedProperty.key, 'is', value)}
                        >
                          <span className="item-label">{label}</span>
                        </button>
                      );
                    })
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {hoveredText && (
        <div 
          className="faceted-filter-tooltip animate-in"
          style={{ 
            position: 'fixed',
            top: tooltipCoords.top,
            left: tooltipCoords.left,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none'
          }}
        >
          {hoveredText}
        </div>
      )}
    </div>
  );
};

export default FacetedFilter;
